import {useTranslation} from "react-i18next";
import {useEffect, useRef, useState, useContext, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import NavBreadcrum from "components/NavBreadcrum";
import navs from "./navs";
import axiApi from "ApiCaller";
import TableAlertsManager from "./viewAlerts/TableAlertsManager";
import TopBarActions from "components/TopBarActions";
import ButtonsActions from "../utils/buttonsActions";
import BoxFiltersManager from "./viewAlerts/BoxFiltersManager";
import {SwalAlertChoice, SwalAlertClass} from "core/swalClass";
import UrlQueryParams from "../utils/urlQueryParams";
import AuthContext from "context/AuthContext";
import LoaderSpinner from "context/LoaderSpinner";
import FilterModel from "components/FilterModel";
import TopBarPagination from "components/paginator/TopBarPagination-v2";
import {PagerModel} from "components/paginator/PaginationModel";
import OrderingModel from "components/ordering/OrderingModel";
import ItemsModel from "core/itemsModel";

const AlertsView = () => {
    const {t: _Tr} = useTranslation("routes");
    const {user} = useContext(AuthContext);
    const bread = useMemo(
        () => [
            {name: _Tr("labels.alerts"), url: "/alerts/", current: false},
            {name: _Tr("labels.alerts"), current: true},
        ],
        [_Tr],
    );
    document.title = _Tr("labels.alerts");
    const [urlQuery] = useSearchParams();
    const [itemsShown, setItemsShown] = useState([]);
    const [buttonsTB, setButtonsTB] = useState({
        ...new ButtonsActions(),
        title: document.title,
    });
    const items = useRef(new ItemsModel());
    const filters = useRef(new FilterModel());
    const pagerModelRef = useRef(new PagerModel());
    const orderingRef = useRef(new OrderingModel());

    const [item, setItem] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    //table
    const [checkAll, setCheckAll] = useState(false);
    // const [showPrev, setShowPrev] = useState(false);

    useEffect(() => {
        const canEdit = user && (user.is_superuser || user.is_admin); // Verificar si el usuario es admin o superusuario

        let buttons = {...buttonsTB};
        buttons.title = document.title;
        buttons.filter.active = true;
        buttons.filter.onClick = onClickFilter;

        buttons.delete.active = canEdit; // Solo mostrar si es admin
        buttons.delete.disabled = true; // Inicialmente deshabilitado
        buttons.delete.onClick = onCLickDelete;

        //buttons.settings.active = true;
        setButtonsTB(buttons);
        pagerModelRef.current.active = true;
        pagerModelRef.current.handleCallApi = () => loadAlertsData();
        orderingRef.current.applyOrder = () => loadAlertsData();
        items.current.setItem = setItem;
        items.current.setShowPrev = () => {};
        items.current.item = {};
        // Cargar datos directamente con POST (usa filtros por defecto de BoxFilters)
        loadGetAlertsData();
    }, []);

    filters.current.filter = () => {
        pagerModelRef.current.paging.page = 1;
        loadAlertsData();
    };
    const loadGetAlertsData = () => {
        setIsLoading(true);

        axiApi
            .get("/api/alerts/data/", {params: filters.current.chosen})
            .then((resp) => {
                items.current.showed = [...resp.data.items];
                setItemsShown([...resp.data.items]);
                if (resp.data.pager) {
                    pagerModelRef.current.paging.update(resp.data.pager);
                }
                filters.current.list = {...resp.data.params.filters};
                new UrlQueryParams(urlQuery, "uid").setSearchParams(filters);
            })
            .catch((error) => {
                console.error("Error al cargar alertas:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const loadAlertsData = () => {
        setIsLoading(true);
        axiApi
            .post("/api/alerts/data/", filters.current.chosen, {
                params: {...pagerModelRef.current.paging.getParams(), ...orderingRef.current.getParams()},
            })
            .then((resp) => {
                items.current.setShowed(resp.data.items);
                setItemsShown([...resp.data.items]);
                if (resp.data.pager) {
                    pagerModelRef.current.paging.update(resp.data.pager);
                }
            })
            .catch((error) => {
                console.error("Error al cargar alertas:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    function onClickFilter() {
        let buttons = {...buttonsTB};
        buttons.filter.click = !buttonsTB.filter.click;
        buttons.title = document.title;
        buttons.counter = items.current.showed.length;
        setButtonsTB(buttons);
    }

    function onCLickDelete() {
        const alert = new SwalAlertChoice();
        alert.params.confirmButtonText = "Borrar";
        alert.params.cancelButtonText = "Cancelar";
        alert.warning("Atención", "Se borrarán los items seleccionados").then((result) => {
            if (result.isConfirmed) {
                deleteItems();
            }
        });
    }

    function deleteItems() {
        let itemsDelete = items.current.getChosenIds();
        setIsLoading(true);
        axiApi
            .post(`/api/alerts/delete/`, {items: itemsDelete})
            .then((resp) => {
                if (!resp.data.status) {
                    const alert = new SwalAlertClass({confirmButtonText: "Aceptar"});
                    alert.error("Error", "Inténtelo de nuevo más tarde o consulte con su administrador");
                } else {
                    loadAlertsData();
                    const alert = new SwalAlertClass({confirmButtonText: "Aceptar"});
                    alert.success("Elementos borrados");
                    items.current.clearChosen();
                    let buttons = {...buttonsTB};
                    buttons.delete.disabled = true;
                    setButtonsTB(buttons);
                }
            })
            .catch((error) => {
                console.error("Error al enviar la solicitud:", error);
                // const alert = new SwalAlertClass({ confirmButtonText: "Aceptar" });
                // alert.error("Error", "Error de conexión con el servidor");
            })
            .finally(() => {
                items.current.clearChosen();
                setIsLoading(false);
            });
    }

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <TopBarActions actions={buttonsTB} filtersRef={filters} />
            <BoxFiltersManager
                items={items}
                setItemsShown={setItemsShown}
                showFilter={buttonsTB.filter.click}
                filters={filters.current}
                filterByDate={true}
                setButtonsTB={setButtonsTB}
                buttonsTB={buttonsTB}
            />
            <TopBarPagination pagerModel={pagerModelRef.current} stickyTopValue="125px" chosen={items.current.chosen} />
            <div className="row" style={buttonsTB.filter.click ? {marginTop: "20px"} : {}}>
                <LoaderSpinner isLoading={isLoading}>
                    <TableAlertsManager
                        itemsShow={itemsShown}
                        items={items}
                        checkAll={checkAll}
                        setCheckAll={setCheckAll}
                        buttonsTB={buttonsTB}
                        setButtonsTB={setButtonsTB}
                        orderingRef={orderingRef}
                    />
                    <label className={itemsShown.length === 0 ? "" : "d-none"}>No hay resultados que mostrar</label>
                </LoaderSpinner>
            </div>
        </>
    );
};
export default AlertsView;
